import React from 'react';
import { Alert, Box, Button, Chip, Divider, LinearProgress, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import {
  canRunGuidedRecovery,
  chipColor,
  CommissionJob,
  formatBringUpFailure,
  formatBringUpRemediation,
  formatStepSecondary,
  getBringUpFailureDiagnostics,
  getBringUpFailureFacts,
  JobStep,
  RunnerProgress,
  STEP_LABELS,
  STEP_ORDER,
} from './bringUpShared';

type BringUpTimelineProps = {
  job: CommissionJob | null;
  jobId: string;
  jobStatus: string;
  jobIsRunning: boolean;
  runnerProgress: RunnerProgress | null;
  stepById: Map<string, JobStep>;
  readyForProduction: boolean;
  bootstrapOnlyJob: boolean;
  bootstrapComplete: boolean;
  failedStep: JobStep | null;
  canWriteDevices: boolean;
  selectedDeviceId: string;
  openGuidedRecoveryDialog: () => void;
  onOpenDevices: () => void;
};

export default function BringUpTimeline({
  job,
  jobId,
  jobStatus,
  jobIsRunning,
  runnerProgress,
  stepById,
  readyForProduction,
  bootstrapOnlyJob,
  bootstrapComplete,
  failedStep,
  canWriteDevices,
  selectedDeviceId,
  openGuidedRecoveryDialog,
  onOpenDevices,
}: BringUpTimelineProps) {
  const failureFacts = getBringUpFailureFacts(job, failedStep);
  const failureDiagnostics = getBringUpFailureDiagnostics(failedStep);
  const remediation = formatBringUpRemediation(failedStep);
  const canRecover = canRunGuidedRecovery(failedStep);

  return (
    <>
      {!job && (
        <Alert severity="info">No bring-up run yet for this device. Click Start Bring-up to begin.</Alert>
      )}
      {job && (
        <>
          {jobIsRunning && <LinearProgress sx={{ mb: 1.5 }} />}
          <Stack direction="row" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
            <Typography variant="subtitle2">Job status:</Typography>
            <Chip size="small" label={jobStatus || 'unknown'} color={chipColor(jobStatus)} />
            <Typography variant="caption" color="text.secondary">
              {jobId}
            </Typography>
          </Stack>
          {jobIsRunning && runnerProgress && (
            <Alert
              severity={Number(runnerProgress.idle_seconds || 0) >= 45 ? 'warning' : 'info'}
              sx={{ mb: 1.5 }}
            >
              Runner progress:
              {runnerProgress.current_step ? ` ${runnerProgress.current_step}` : ' working'}
              {runnerProgress.current_step_status ? ` (${runnerProgress.current_step_status})` : ''}
              {runnerProgress.idle_seconds != null ? ` • no new output for ${runnerProgress.idle_seconds}s` : ''}
            </Alert>
          )}
          <List dense>
            {STEP_ORDER.map((stepId) => {
              const step = stepById.get(stepId);
              const status = String(step?.status || 'pending');
              return (
                <ListItem key={stepId} divider>
                  <ListItemText
                    primary={
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {STEP_LABELS[stepId]}
                        </Typography>
                        <Chip size="small" label={status} color={chipColor(status)} />
                      </Stack>
                    }
                    secondary={formatStepSecondary(stepId, step)}
                  />
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 1.5 }} />

          {readyForProduction && (
            <Alert severity="success" icon={<CheckIcon />}>
              Device is ready for production. Strict preflight and commissioning gate passed.
            </Alert>
          )}
          {!readyForProduction && bootstrapOnlyJob && bootstrapComplete && (
            <Alert severity="success" icon={<CheckIcon />}>
              Identity bootstrap completed. Trust, claim, and edge credentials are in place; run full Bring Up next
              to install the runtime and pass production gates.
            </Alert>
          )}
          {!readyForProduction && jobStatus === 'failed' && (
            <Alert severity="error">
              <Box>
                <Box>{formatBringUpFailure(job, failedStep)}</Box>
                {failureFacts.length > 0 && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                      Failure details
                    </Typography>
                    <Stack gap={0.5}>
                      {failureFacts.map((fact) => (
                        <Typography key={`${fact.label}:${fact.value}`} variant="body2">
                          <strong>{fact.label}:</strong> {fact.value}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
                {failureDiagnostics && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Technical diagnostics
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'grey.100',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: '0.75rem',
                      }}
                    >
                      {failureDiagnostics}
                    </Box>
                  </Box>
                )}
                {remediation && <Box sx={{ mt: 0.75 }}>{remediation}</Box>}
                {canRecover && (
                  <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.25 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={openGuidedRecoveryDialog}
                      disabled={!canWriteDevices || !selectedDeviceId}
                    >
                      Run Guided Recovery
                    </Button>
                    <Button size="small" variant="outlined" onClick={onOpenDevices}>
                      Open Claim / Manage Devices
                    </Button>
                  </Stack>
                )}
              </Box>
            </Alert>
          )}
          {!readyForProduction && jobStatus === 'succeeded' && !bootstrapOnlyJob && (
            <Alert severity="warning">
              Bring-up succeeded but is not marked production-ready. Review preflight and gate details in Setup
              Preflight before staging cameras, zones, or pack-driven policies.
            </Alert>
          )}
        </>
      )}
    </>
  );
}
